import React, { useState } from "react";
import { Lock, Eye, EyeOff, Check, X } from "lucide-react";

import Input from "src/components/ui/Input";
import { PASSWORD_RULES } from "src/utils/authFormValidation";

interface PasswordFieldProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string | null;
  label?: string;
  placeholder?: string;
  defaultHelperText?: string;
}

// Shared by the sign-up and admin tenant-creation wizards — shows a live
// rules checklist while the field is focused, per PASSWORD_RULES.
const PasswordField: React.FC<PasswordFieldProps> = ({
  value,
  onChange,
  onBlur,
  error,
  label = "Password",
  placeholder = "At least 8 characters",
  defaultHelperText,
}) => {
  const [show, setShow] = useState(false);
  const [focused, setFocused] = useState(false);

  return (
    <div className="relative">
      <Input
        label={label}
        required
        type={show ? "text" : "password"}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          onBlur?.();
        }}
        startAdornment={<Lock size={16} />}
        endAdornment={
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="flex items-center"
          >
            {show ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        }
        error={!!error}
        helperText={error || defaultHelperText}
      />

      {focused && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1.5 rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800">
          <div className="space-y-1.5">
            {PASSWORD_RULES.map((rule) => {
              const passed = rule.test(value);
              return (
                <div key={rule.label} className="flex items-center gap-1.5 text-xs">
                  {passed ? (
                    <Check size={13} className="shrink-0 text-green-500" />
                  ) : (
                    <X size={13} className="shrink-0 text-gray-300 dark:text-gray-600" />
                  )}
                  <span
                    className={
                      passed
                        ? "text-gray-600 dark:text-gray-300"
                        : "text-gray-400 dark:text-gray-500"
                    }
                  >
                    {rule.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PasswordField;
